'use strict';

const Promise = require('bluebird');

function CourseWebsocket(options) {
    this.userId = options.userId;
    this.courseId = options.courseId;

    this.lockId = null;
    this.keepAliveID = -1;

    this.sock = null;
    this.isConnecting = false;
    this._isOpen = false;
    this._isAuthenticated = false;
    this._haveLock = false;
}

CourseWebsocket.prototype.connectAuthLock = function(reconnect) {
    const self = this;
    this.isConnecting = true;

    return this.wsConnectAuth(reconnect)
        .then(function() {
            return self.sendLockRequest(reconnect);
        })
        .then(function() {
            self._startKeepAlive();
        })
        .finally(function() {
            self.isConnecting = false;
        });
}

CourseWebsocket.prototype.wsConnectAuth = function(reconnect) {
    console.debug(`wsConnect(reconnect=${reconnect})`);
    var self = this;
    var _connectUri = '';
    var _connectPath = '';

    function buildConnectUrl() {
        return GlobalConfig.DevMode ? "//" + _connectUri : "wss://" + _connectUri;
    }

    function buildConnectPath() {
        return _connectPath + "/ws";
    }

    function getConnectUrl() {
        return new Promise(function(resolve, reject) {
            $.get(GlobalConfig.ApiUrl + "/websocket/connect_uri")
                .done(function(data) {
                    _connectUri = data.uri;
                    _connectPath = data.path;
                    resolve();
                })
                .fail(function(xhr, textStatus, errorThrown) {
                    reject({
                        step: "ws_url",
                        error: "Unable to get connectUrl from " + GlobalConfig.ApiUrl + "/websocket/connect_uri: " + xhr.responseText
                    });
                });
        });
    }

    function setupNewWs() {
        const connectUrl = buildConnectUrl();
        const path = buildConnectPath();
        console.debug(`setupWsAndConnect() using: ${connectUrl}${path}`);

        self.sock = io(connectUrl, {
            timeout: GlobalConfig.WebSocketOptions.timeout,
            reconnection: false,
            autoConnect: false,
            path: path,
            multiplex: false
        });
    }

    function connectWs() {
        return new Promise(function(resolve, reject)  {
            function connectCallback(param) {
                console.debug("connect, connected");
                self.sock.removeListener("connect", connectCallback);

                self._isOpen = true;
                var authDelay = 777; // slight delay before sending authenticate message (so we aren't sending messages at the same time as initial handshaking)
                window.setTimeout(function() {
                    resolve();
                }, authDelay)
            }

            function disconnectCallback(param) {
                console.warn("disconnect, connection_lost:", param);
                self.sock.removeListener("disconnect", disconnectCallback);
                //possible param: 'transport close'

                self._isOpen = false;
                self._isAuthenticated = false;
                self._haveLock = false;
                reject({ step: "ws_connect", error: "Can't establish connection to server. (disconnect)" });
            }

            function connectErrorCallback() {
                console.warn("connect_error");
                self.sock.removeListener("connect_error", connectErrorCallback);

                self._isOpen = false;
                self._isAuthenticated = false;
                self._haveLock = false;
                reject({ step: "ws_connect", error: "Can't establish connection to server. (connect_error)" });
            }

            function connectTimeoutCallback() {
                console.warn("connect_timeout");
                self.sock.removeListener("connect_timeout", connectTimeoutCallback);

                self._isOpen = false;
                self._isAuthenticated = false;
                self._haveLock = false;
                reject({ step: "ws_connect", error: "Can't establish connection to server. (connect_timeout)" });
            }

            self.sock.on("connect", connectCallback);
            self.sock.on("disconnect", disconnectCallback);
            self.sock.on("connect_error", connectErrorCallback);
            self.sock.on("connect_timeout", connectTimeoutCallback);


            //since we're manually reconnecting, these shouldn't be fired (todo: remove)
            self.sock.on("reconnect_attempt", function() { //synonymous
                console.warn("reconnect_attempt");
            });

            self.sock.on("reconnecting", function() { //synonymous
                console.warn("reconnecting");
            });

            self.sock.on("reconnect", function() {
                console.warn("reconnect");
            });

            self.sock.on("reconnect_failed", function() {
                console.warn("reconnect_failed");
            });

            self.sock.connect();
        });
    }

    function authWS() {
        console.debug("in authWS");
        return new Promise(function(resolve, reject) {
            //wait for authentication to succeed before resolving
            const auth = {
                responded: false,
                success: false
            }

            function tryResolve(res) {
                if (auth.responded) {
                    if (auth.success) {
                        self.sock.removeListener("msg", wsAuthRespHandler);
                        resolve();
                    }
                    else {
                        self.sock.removeListener("msg", wsAuthRespHandler);
                        self.disconnect().then(function() {
                            reject({ step: "test_course_lock", error: res });
                        });
                    }
                }
                else {
                    return;
                }
            }

            function wsAuthRespHandler(msg) {
                switch (msg.type) {
                    case "auth_fail": {
                        //immediately reject on auth_fail
                        console.error("auth_fail callback!");
                        auth.responded = true;  //probably don't need this
                        auth.success = false;   //probably don't need this
                        self.sock.removeListener("msg", wsAuthRespHandler);

                        return self.disconnect().then(function() {
                            reject({step: "ws_authenticate", error: msg.reason});
                        });
                    }
                    case "auth_success": {
                        console.debug("auth_success");
                        self._isAuthenticated = true;
                        auth.responded = true;
                        auth.success = true;
                        tryResolve(msg);
                        break;
                    }
                }
            }

            self.sock.on("msg", wsAuthRespHandler);

            if (
                !self.sock.emit(
                    "msg",
                    JSON.stringify({
                        type: "authenticate",
                        course_id: self.courseId,
                        user_id: self.userId,
                    })
                )
            ) {
                self.sock.removeListener("msg", wsAuthRespHandler);
                reject({ step: "ws_authenticate", error: "Could not send authentication message." });
                return;
            }
        });
    }

    return new Promise(function(resolve, reject){
        getConnectUrl()
            .then(function() {
                if(!reconnect) {
                    setupNewWs();
                }
                else {
                    //hack: updates socket connect path
                    //needed during rolling restart when path changes
                    //!assumes that only socket path will change: relies upon direct.muzzylane.com/[somePath] being websocket destination
                    console.log("[reconnect] update sock path. PRE:", self.sock);
                    self.sock.io.opts.path = buildConnectPath();
                    console.log("[reconnect] update sock path. POST:", self.sock);
                }

                return connectWs();
            })
            .then(function() {
                return authWS();
            })
            .then(function() {
                resolve();
            })
            .catch(function(err) {
                // console.error(err);
                reject(err);
            });
    });
}

CourseWebsocket.prototype.sendLockRequest = function(reconnect) {
    const MAX_ATTEMPTS = 3;
    let attempt = 1;
    const self = this;
    console.debug(`sendLockRequest(reconnect=${reconnect})`);

    const sendReq = Promise.method(function(rejectHandler) {
        console.debug("sending request_course_lock message. attempt " + attempt + "...");
        if (
            !self.sock.emit(
                "msg",
                JSON.stringify({
                    type: "request_course_lock",
                    course_id: self.courseId,
                    userId: self.userId,
                    lockId: self.lockId ? self.lockId : null, //need this?
                    timeout: 5000
                })
            )
        ) {
            console.warn('rejectHandler (sendLockRequest.sendReq)');
            rejectHandler({step: "request_course_lock", error: "Requesting course lock failed."});
            return;
        }
    });

    const sendReconnectReq = Promise.method(function(rejectHandler) {
        console.debug("sending reestablish_course_lock message. attempt " + attempt + "...");
        if (
            !self.sock.emit(
                "msg",
                JSON.stringify({
                    type: "reestablish_course_lock",
                    course_id: self.courseId,
                    userId: self.userId,
                    lockId: self.lockId ? self.lockId : null,
                    timeout: 5000
                })
            )
        ) {
            console.warn('rejectHandler (sendLockRequest.sendReconnectReq)');
            rejectHandler({step: "reestablish_course_lock", error: "Reestblishing course lock failed."});
            return;
        }
    });

    return new Promise(function(resolve, reject) {
        self.sock.on("msg", function lockRefRespHandler(msg) {
            switch(msg.type) {
                case "action_succeeded":
                    self._haveLock = true;
                    self.lockId = msg.current_state_data.lockId;
                    self.sock.removeListener("msg", lockRefRespHandler);
                    resolve();
                    break;
                case "action_failed":
                    attempt += 1;

                    if (attempt <= MAX_ATTEMPTS) {
                        return new Promise(function(resolve, reject) {
                            setTimeout(function() {
                                if (!reconnect) {
                                    return sendReq();
                                }
                                else {
                                    return sendReconnectReq();
                                }
                            }, GlobalConfig.WebSocketOptions.lockRetryDelay);
                        });
                    }
                    else {
                        self.disconnect().then(function() {
                            console.debug("AFTER self.disconnect()");
                            self.sock.removeListener("msg", lockRefRespHandler);
                            reject({step: 'request_course_lock', error: msg.error});
                        });
                    }
                    break;
            }
        });

        if (!reconnect) {
            return sendReq();
        }
        else {
            return sendReconnectReq();
        }

    });
}

CourseWebsocket.prototype.refreshLock = function() {
    return this.sendLockRefreshRequest();
}


CourseWebsocket.prototype.sendLockRefreshRequest = function() {
    const self = this;
    const MAX_ATTEMPTS = 3;
    let attempt = 1;

    function sendReq(rejectHandler) {
        // console.log("sending refresh_course_lock message. attempt " + attempt + "...");
        if (
            !self.sock.emit(
                "msg",
                JSON.stringify({
                    type: "refresh_course_lock",
                    course_id: self.courseId,
                    userId: self.userId,
                    timeout: 5000,
                })
            )
        ) {
            console.log('rejectHandler (sendLockRefreshRequest.sendReq)');
            rejectHandler({step: "refresh_course_lock", error: "Refresh course lock failed."});
            return;
        }
    }

    return new Promise(function(resolve, reject) {
        self.sock.on("msg", function lockReqRespHandler(msg) {
            switch (msg.type) {
                case "action_succeeded":
                    self._haveLock = true;
                    self.lockId = msg.current_state_data.lockId;
                    self.sock.removeListener("msg", lockReqRespHandler);
                    resolve();
                    break;
                case "action_failed":
                    attempt += 1;

                    if (attempt <= MAX_ATTEMPTS) {
                        sendReq(reject);
                    }
                    else {
                        self.disconnect().then(function() {
                            self.sock.removeListener("msg", lockReqRespHandler);
                            reject(msg.type, msg.error);
                        });
                    }
                    break;
            }
        });

        sendReq();
    });
}


CourseWebsocket.prototype._startKeepAlive = function(actWebsocket) {
    const self = this;
    this._stopKeepAlive();
    const keepAliveInterval = 40 * 1000; // 40 seconds (less than 60 second ELB timeout)

    self.keepAliveID = window.setInterval(function() {
        if (self._isOpen) {
            self.sock.emit("msg", JSON.stringify({ type: "keep_alive" }));
        }
    }, keepAliveInterval);
}

CourseWebsocket.prototype._stopKeepAlive = function(actWebsocket) {
    if (self.keepAliveID >= 0) {
        window.clearInterval(self.keepAliveID);
        self.keepAliveID = -1;
    }
}

CourseWebsocket.prototype.releaseLock = function() {
    const self = this;
    return this._sendRelaseLock()
        .finally(function() { // disconnect websocket regardless of _sendRelaseLock outcome
            return self.disconnect();
        });
}

CourseWebsocket.prototype.disconnect = function() {
    const self = this;
    return new Promise(function(resolve, reject) {
            self._stopKeepAlive();
            self.sock.removeAllListeners("disconnect");
            self.sock.disconnect();
            // self.sock = null;
            self._isOpen = false;
            self._isAuthenticated = false;
            self._haveLock = false;
            resolve();
        });
}

CourseWebsocket.prototype._sendRelaseLock = function() {
    const self = this;
    console.debug("in _sendRelaseLock");
    return new Promise(function(resolve, reject) {
        function lockRelRespHandler(msg) {
            switch (msg.type) {
                case "action_succeeded": {
                    self.sock.removeListener("msg", lockRelRespHandler);
                    self._haveLock = false;
                    resolve();
                    break;
                }
                case "action_failed": {
                    self.sock.removeListener("msg", lockRelRespHandler);
                    reject(msg.type, msg.error);
                    break;
                }
            }
        }

        self.sock.on("msg", lockRelRespHandler);

        if (
            !self.sock.emit(
                "msg",
                JSON.stringify({
                    type: "release_course_lock",
                    course_id: self.courseId
                })
            )
        ) {
            self.sock.removeListener("msg", lockRelRespHandler);
            reject({ step: "release_course_lock", error: "Release course lock failed." });
            return;
        }
    })
}

module.exports = CourseWebsocket;